import React, { useState } from 'react';

/**
 * SubscriptionBanner - Shows a floating banner during trial period.
 * Displays days remaining and a link to subscribe.
 */
const POLICY_TEXT = [
  { title: '7-Day Trial Access', body: 'Users may access the application during a 7-day trial period. At the end of the trial period, access to the application will be automatically suspended unless a subscription or access plan is purchased.' },
  { title: 'Purchase of Access', body: 'By purchasing access to the application, the user acknowledges that access to the digital service is granted immediately upon successful payment.' },
  { title: 'No Refunds After Access Is Granted', body: 'Because the application is a digital service that becomes immediately available upon purchase, all sales are final. Once paid access has been granted, no refunds, credits, or partial refunds will be provided, regardless of the extent of use, non-use, course withdrawal, schedule changes, academic performance, or any other reason.' },
  { title: 'Technical Issues', body: 'If a user experiences technical issues that prevent access to the application, the user should contact support promptly. We will make reasonable efforts to resolve verified technical problems. Resolution of technical issues does not entitle the user to a refund.' },
  { title: 'Agreement', body: 'By purchasing access to the application, the user acknowledges that they have read, understood, and agreed to this Refund and Access Policy.' },
];

function SubscriptionBanner({ trialDaysLeft, onSubscribeClick }) {
  const [redirecting, setRedirecting] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  if (trialDaysLeft === null || trialDaysLeft === undefined || trialDaysLeft < 0) {
    return null;
  }

  const isUrgent = trialDaysLeft <= 2;

  return (
    <div style={{
      position: 'fixed',
      top: '2px',
      left: '16px',
      backgroundColor: '#fef9c3',
      border: `2px solid ${isUrgent ? '#dc2626' : '#f59e0b'}`,
      borderRadius: '12px',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      zIndex: 9999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      maxWidth: '420px',
      animation: isUrgent ? 'pulse 2s infinite' : 'none',
    }}>
      <span style={{ fontSize: '24px' }}>{isUrgent ? '⚠️' : '⏳'}</span>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: '700',
          fontSize: '14px',
          color: '#6b1010',
          marginBottom: '2px',
        }}>
          {trialDaysLeft === 0
            ? 'Trial expires today!'
            : `Free trial: ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining`
          }
        </div>
        <div style={{
          fontSize: '12px',
          color: '#6b1010',
        }}>
          Subscribe to keep access after your trial ends.
        </div>
        <button
          onClick={() => setShowPolicy(true)}
          style={{
            background: 'none', border: 'none', padding: 0, marginTop: '4px',
            color: '#6b1010', fontSize: '11px', cursor: 'pointer',
            textDecoration: 'underline', textUnderlineOffset: '2px', opacity: 0.75
          }}
          onMouseEnter={(e) => e.target.style.opacity = 1}
          onMouseLeave={(e) => e.target.style.opacity = 0.75}
        >
          Access and Refund Policy
        </button>
      </div>
      <button
        onClick={async () => { setRedirecting(true); try { await onSubscribeClick(); } catch { setRedirecting(false); } }}
        disabled={redirecting}
        style={{
          padding: '6px 14px',
          fontSize: '12px',
          fontWeight: '700',
          color: '#fff',
          background: redirecting ? '#6b7280' : 'linear-gradient(135deg, #3730a3, #4f46e5)',
          border: 'none',
          borderRadius: '8px',
          cursor: redirecting ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => !redirecting && (e.target.style.transform = 'scale(1.05)')}
        onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
      >
        {redirecting ? '⏳ Redirecting…' : 'Subscribe'}
      </button>

      {/* Full-screen redirect overlay */}
      {redirecting && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(15,23,42,0.92)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 20,
        }}>
          <div style={{
            width: 52, height: 52,
            border: '5px solid rgba(255,255,255,0.15)',
            borderTop: '5px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#e2e8f0', fontSize: 17, fontWeight: 600, margin: 0 }}>
            Redirecting to payment…
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
            You'll be taken to Stripe's secure checkout.
          </p>
        </div>
      )}

      {/* Refund and Access Policy modal */}
      {showPolicy && (
        <div
          onClick={() => setShowPolicy(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#1e1b4b', fontWeight: '700' }}>
              Access and Refund Policy
            </h2>
            {POLICY_TEXT.map((section, i) => (
              <div key={i} style={{ marginBottom: '18px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e1b4b', marginBottom: '6px' }}>
                  {section.title}
                </div>
                <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
                  {section.body}
                </div>
              </div>
            ))}
            <button
              onClick={() => setShowPolicy(false)}
              style={{
                marginTop: '8px',
                padding: '10px 24px',
                backgroundColor: '#4f46e5',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionBanner;
